/**
 * useIntegrationConnect: shared client logic for starting an integration's
 * OAuth connect flow from a settings section.
 *
 * Extracted from the calendars section so any future integration kind (a crm, a
 * ticketing system, ...) reuses the same connect handshake, in-flight guard,
 * and callback error handling rather than re-deriving them. Each section owns
 * one instance, passing the kind slug used in the start url and its own copy
 * map keyed by the provider callback error codes; the copy stays local to the
 * section so wording never leaks across kinds.
 *
 * Why a hook and not a component: the connect handshake, the bfcache guard, and
 * the one-shot callback processing are pure client state with no markup of
 * their own, so a section keeps full control of its layout while sharing the
 * behaviour verbatim.
 *
 * @module components/admin/settings/integrations/useIntegrationConnect
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useOrg } from '../../../../lib/OrgProvider';
import { useSupabase } from '../../../../lib/SupabaseProvider';
import { captureException } from '../../../../lib/telemetry/posthog/frontend';

/** Configuration for a section's connect flow. */
export interface UseIntegrationConnectOptions {
  /**
   * The integration kind slug used in the start url (e.g. 'calendar'). Kept as
   * a plain string so a section can adopt a new kind without this hook needing
   * to know the kind union.
   */
  kind: string;
  /**
   * Short inline copy keyed by the provider callback error code. Unknown codes
   * fall back to a generic failure line, so a section only lists the codes it
   * wants to phrase specially.
   */
  errorCopy: Record<string, string>;
}

/** What a settings section consumes to render its connect controls. */
export interface UseIntegrationConnect {
  /** In-flight guard: true from a connect click until navigation or failure. */
  connecting: boolean;
  /** The single inline error line, or null when there is none. */
  errorMessage: string | null;
  /**
   * Setter for the inline error line. Returned so the section can drive the
   * same line for its own flows (a failed disconnect, say) without a second
   * error slot.
   */
  setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
  /** Start (or restart) the connect flow for a provider, then navigate to it. */
  handleConnect: (provider: string) => Promise<void>;
}

/** Retains router metadata while excluding non-object navigation state. */
function navigationState(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

/** Reads only this integration's callback code from the current history entry. */
function savedCallbackError(value: unknown, kind: string): string | null {
  const saved = navigationState(navigationState(value).integrationConnectionError);
  return saved.kind === kind && typeof saved.code === 'string' ? saved.code : null;
}

/**
 * Own the connect handshake, the in-flight guard, and the one-shot callback
 * processing for one integration kind. See the module doc for the contract.
 */
export function useIntegrationConnect({
  kind,
  errorCopy,
}: UseIntegrationConnectOptions): UseIntegrationConnect {
  const { activeOrg } = useOrg();
  const { supabase } = useSupabase();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const [connecting, setConnecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(() => {
    const saved = savedCallbackError(location.state, kind);
    return saved ? errorCopy[saved] ?? 'connection failed, try again' : null;
  });

  /** Guards the one-shot read/clear of the callback query params. */
  const paramsProcessed = useRef(false);

  // handleConnect navigates away with the in-flight flag still set. Pressing
  // Back from the consent screen restores this page from bfcache with that flag
  // frozen true, leaving Connect/Reconnect permanently disabled. A persisted
  // pageshow is the one signal for that restore, so clear the flag on it.
  useEffect(() => {
    const onPageShow = (event: PageTransitionEvent): void => {
      if (event.persisted) setConnecting(false);
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Read the callback result once, then quietly strip `connected`/`error` from
  // the url while keeping every other param (e.g. section=...) so the page
  // scroll still lands where the callback returned. Keep the error code in this
  // history entry because org/auth loading can unmount settings after this effect.
  useEffect(() => {
    if (paramsProcessed.current) return;
    const section = searchParams.get('section');
    if (section && section !== (kind === 'calendar' ? 'calendars' : kind)) return;
    paramsProcessed.current = true;
    const errorCode = searchParams.get('error');
    const connected = searchParams.get('connected');
    if (errorCode) {
      setErrorMessage(errorCopy[errorCode] ?? 'connection failed, try again');
    } else if (connected) {
      setErrorMessage(null);
    }
    if (errorCode || connected) {
      const next = new URLSearchParams(searchParams);
      next.delete('error');
      next.delete('connected');
      const state = { ...navigationState(location.state) };
      if (errorCode) state.integrationConnectionError = { kind, code: errorCode };
      else delete state.integrationConnectionError;
      setSearchParams(next, { replace: true, state });
    }
  }, [searchParams, setSearchParams, errorCopy, kind, location.state]);

  /** A fresh Authorization header from the current session. */
  const getBearer = useCallback(async (): Promise<string> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');
    return `Bearer ${session.access_token}`;
  }, [supabase]);

  const handleConnect = useCallback(
    async (provider: string): Promise<void> => {
      if (!activeOrg) return;
      setConnecting(true);
      setErrorMessage(null);
      if (savedCallbackError(location.state, kind)) {
        const state = { ...navigationState(location.state) };
        delete state.integrationConnectionError;
        setSearchParams(searchParams, { replace: true, state });
      }
      try {
        const bearer = await getBearer();
        // The host's browser timezone is captured at connect so bookings render
        // in a real IANA zone; the callback persists it onto the connection.
        const timezone = encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);
        const res = await fetch(
          `/api/integrations/${kind}/${provider}/start?organizationId=${activeOrg.id}&timezone=${timezone}`,
          { headers: { Authorization: bearer } },
        );
        if (!res.ok) throw new Error(`start failed: ${res.status}`);
        const json = (await res.json()) as { url: string };
        window.location.assign(json.url);
      } catch (err) {
        captureException(
          err instanceof Error ? err : new Error(String(err)),
          'useIntegrationConnect.handleConnect',
          { orgId: activeOrg.id, kind, provider },
        );
        setErrorMessage('connection failed, try again');
        setConnecting(false);
      }
    },
    [activeOrg, getBearer, kind, location.state, searchParams, setSearchParams],
  );

  return { connecting, errorMessage, setErrorMessage, handleConnect };
}
